import React from 'react';
import { ConfigProvider } from 'antd';
import { useTheme } from '../../../theme/ThemeContext';
import { AgenticPlannerTasks } from '../AskAsperAgentic.types';

export interface PlannerTasksBoxProps {
    plannerTasks: AgenticPlannerTasks;
    'data-test'?: string;
    'data-test-id'?: string;
}

/**
 * PlannerTasksBox - Displays grey box with tasks that will be executed next
 *
 * This component displays a grey box containing tasks outputted by the planner agent,
 * showing what will be executed next.
 */
const PlannerTasksBox: React.FC<PlannerTasksBoxProps> = ({
    plannerTasks,
    'data-test': dataTest,
    'data-test-id': dataTestId,
}) => {
    const theme = useTheme();

    // Helper function to render text with bold markers
    const renderBoldText = (text: string) => {
        const parts = text.split(/(\*\*.*?\*\*)/g);
        return parts.map((part, index) => {
            if (part.startsWith('**') && part.endsWith('**')) {
                const boldText = part.slice(2, -2);
                return (
                    <strong key={index} className="font-semibold">
                        {boldText}
                    </strong>
                );
            }
            return <span key={index}>{part}</span>;
        });
    };

    return (
        <ConfigProvider theme={theme}>
            <div
                className="bg-[#F5F5F5] rounded-lg px-4 py-3"
                data-test={dataTest}
                data-test-id={dataTestId}
                data-planner-tasks-box
            >
                <ul className="space-y-2 list-none pl-0">
                    {plannerTasks.tasks.map((task, index) => (
                        <li
                            key={task.id || index}
                            className="font-['Open_Sans'] text-[16px] leading-[24px] text-[rgba(0,0,0,0.88)] flex items-start"
                            data-test-id={`${dataTestId}-task-${index}`}
                        >
                            <span className="mr-2 mt-1.5">•</span>
                            <span>{renderBoldText(task.text)}</span>
                        </li>
                    ))}
                </ul>
            </div>
        </ConfigProvider>
    );
};

export default PlannerTasksBox;
